import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead, FAQSchema } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Users, MapPin, Clock, CheckCircle, ArrowRight, Award } from 'lucide-react';

const API_URL = "";

const ArtWorkshopsSanAntonio = () => {
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkshops();
  }, []);

  const fetchWorkshops = async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/workshops`);
      if (response.ok) {
        const data = await response.json();
        setWorkshops(data.filter(w => w.status !== 'cancelled').slice(0, 6));
      }
    } catch (error) {
      console.error('Error fetching workshops:', error);
    } finally {
      setLoading(false);
    }
  };

  const faqs = [
    {
      question: "What types of art workshops do you offer in San Antonio?",
      answer: "We offer intensive multi-day workshops covering oil painting, portrait painting, landscape painting, figure drawing, and specialized techniques taught by visiting master artists from across the country."
    },
    {
      question: "How long are the art workshops?",
      answer: "Most workshops run 3-5 days, providing intensive instruction and practice time. Some specialized workshops may be shorter (1-2 days) or longer depending on the subject matter."
    },
    {
      question: "What skill level do I need for workshops?",
      answer: "Workshops vary in level. Many welcome intermediate to advanced students, while others are designed for all levels. Each workshop listing specifies the recommended skill level."
    },
    {
      question: "Are materials included in workshop fees?",
      answer: "Workshop fees typically cover instruction only. A detailed supply list is provided upon registration so students can prepare with appropriate materials."
    }
  ];

  return (
    <main className="min-h-screen bg-white">
      <SEOHead
        title="Art Workshops San Antonio | Painting & Drawing Intensives"
        description="Join intensive art workshops in San Antonio at The Coppini Academy. Learn from visiting master artists in portrait painting, landscape, figure drawing, and more."
        keywords="art workshops San Antonio, painting workshops Texas, oil painting workshop, portrait painting class, master artist workshop, intensive art training San Antonio"
        canonicalPath="/art-workshops-san-antonio"
      />
      <FAQSchema faqs={faqs} />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-white py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1 bg-cafa-red/20 text-cafa-gold text-sm font-medium rounded-full mb-6">
              Learn from Master Artists
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 leading-tight">
              Art Workshops in San Antonio
            </h1>
            <p className="text-xl text-stone-300 mb-8 leading-relaxed">
              Accelerate your artistic development with intensive multi-day workshops. 
              Study with nationally recognized artists in portrait painting, landscape, 
              figure work, and specialized techniques.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Link to="/workshops">View All Workshops <ArrowRight className="ml-2" size={18} /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/membership">Member Discounts</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Workshops */}
      <section className="py-16 lg:py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
              Why Attend Art Workshops?
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Intensive workshops offer unique learning opportunities that regular classes cannot provide.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Award,
                title: "Master Artists",
                desc: "Learn directly from accomplished painters who exhibit nationally"
              },
              {
                icon: Clock,
                title: "Intensive Focus",
                desc: "3-5 days of concentrated study accelerates your progress"
              },
              {
                icon: Users,
                title: "Small Groups",
                desc: "Limited enrollment ensures personal attention and feedback"
              },
              {
                icon: Calendar,
                title: "Flexible Schedule",
                desc: "Workshops throughout the year fit various schedules"
              }
            ].map((benefit, idx) => (
              <Card key={idx} className="border-stone-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <benefit.icon className="mx-auto mb-4 text-cafa-red" size={40} />
                  <h3 className="font-semibold text-stone-900 mb-2">{benefit.title}</h3>
                  <p className="text-stone-600 text-sm">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Workshops */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4 text-center">
            Upcoming Art Workshops in San Antonio
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto text-center mb-12">
            Register early—our workshops fill quickly.
          </p>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-cafa-red border-t-transparent mx-auto"></div>
              <p className="mt-4 text-stone-600">Loading workshops...</p>
            </div>
          ) : workshops.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workshops.map((workshop) => (
                <Card key={workshop.id} className="border-stone-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-xs bg-cafa-red/10 text-cafa-red px-2 py-1 rounded">
                        {workshop.duration || '3-5 Days'}
                      </span>
                      <span className="font-bold text-cafa-red">${workshop.price}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-stone-900 mb-2">{workshop.title}</h3>
                    <p className="text-sm text-stone-500 mb-3">with {workshop.instructor}</p>
                    <p className="text-stone-600 text-sm mb-4 line-clamp-2">{workshop.description}</p>
                    <div className="flex items-center text-sm text-stone-500 mb-4">
                      <Calendar size={14} className="mr-1" />
                      {new Date(workshop.date).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <Button asChild className="w-full bg-cafa-red hover:bg-cafa-red-900">
                      <Link to="/workshops">Learn More</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-stone-50 rounded-lg">
              <Calendar size={48} className="mx-auto mb-4 text-stone-300" />
              <p className="text-stone-600 mb-4">Check back soon for upcoming workshops!</p>
              <Button asChild className="bg-cafa-red hover:bg-cafa-red-900">
                <Link to="/workshops">View Workshop Page</Link>
              </Button>
            </div>
          )}

          <div className="text-center mt-8">
            <Button asChild variant="outline" className="border-cafa-red text-cafa-red hover:bg-cafa-red hover:text-white">
              <Link to="/workshops">View All Workshops</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Workshop Types */}
      <section className="py-16 lg:py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4 text-center">
            Types of Art Workshops We Host
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto text-center mb-12">
            Our workshop program covers diverse subjects and techniques.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Portrait Painting Workshops",
                desc: "Study the human face with expert portrait painters. Learn to capture likeness, render skin tones, and convey character in oil paint.",
                topics: ["Head construction", "Flesh tone mixing", "Lighting for portraits", "Working from life"]
              },
              {
                title: "Landscape & Plein Air",
                desc: "Paint outdoors and in the studio with landscape specialists. Learn atmospheric perspective, composition, and capturing light.",
                topics: ["Outdoor painting setup", "Capturing changing light", "Compositional strategies", "Studio finishing"]
              },
              {
                title: "Figure Painting",
                desc: "Master the painted figure with workshops covering anatomy, gesture, and classical techniques for painting the human form.",
                topics: ["Figure proportion", "Gesture and movement", "Flesh painting", "Compositional figure work"]
              },
              {
                title: "Specialized Techniques",
                desc: "Deep dives into specific methods like alla prima, glazing, underpainting, or working in specific styles.",
                topics: ["Alla prima method", "Classical layering", "Historical techniques", "Contemporary approaches"]
              }
            ].map((type, idx) => (
              <Card key={idx} className="border-stone-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-stone-900 mb-3">{type.title}</h3>
                  <p className="text-stone-600 mb-4">{type.desc}</p>
                  <ul className="grid grid-cols-2 gap-2">
                    {type.topics.map((topic, i) => (
                      <li key={i} className="text-sm text-stone-600 flex items-center">
                        <CheckCircle size={14} className="text-cafa-red mr-2 flex-shrink-0" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 lg:py-20 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif mb-6">
                Workshop Location in San Antonio
              </h2>
              <p className="text-stone-300 mb-6 leading-relaxed">
                All workshops take place at our Monte Vista studio, featuring spacious 
                painting areas, excellent natural light, and a professional atmosphere 
                conducive to focused learning.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <MapPin className="text-cafa-red mr-3 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-medium">The Coppini Academy of Fine Art</p>
                    <p className="text-stone-400">115 Melrose Place</p>
                    <p className="text-stone-400">San Antonio, TX 78212</p>
                    <p className="text-stone-400 mt-2">Phone: (210) 530-8445</p>
                  </div>
                </div>
              </div>
              <p className="text-stone-400 text-sm mb-6">
                Out-of-town visitors: San Antonio offers many accommodation options near our studio. 
                Contact us for recommendations.
              </p>
              <Button asChild className="bg-cafa-red hover:bg-cafa-red-900">
                <Link to="/contact">Get Directions</Link>
              </Button>
            </div>
            <div className="space-y-6">
              <div className="rounded-lg overflow-hidden">
                <iframe
                  src="https://maps.google.com/maps?q=115+Melrose+Place,+San+Antonio,+TX+78212&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Coppini Academy Location - Art Workshops San Antonio"
                  className="grayscale"
                ></iframe>
              </div>
              <div className="bg-stone-800 rounded-lg p-8">
                <h3 className="text-xl font-semibold mb-4">Member Benefits</h3>
              <p className="text-stone-400 mb-6">
                Academy members receive discounts on all workshops, plus early registration access.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "Discounted workshop fees",
                  "Early registration access",
                  "Priority waitlist placement",
                  "Member networking events"
                ].map((benefit, idx) => (
                  <li key={idx} className="flex items-center text-stone-300">
                    <CheckCircle size={16} className="text-cafa-gold mr-2" />
                    {benefit}
                  </li>
                ))}
              </ul>
              <Button asChild className="w-full bg-white text-stone-900 hover:bg-stone-100">
                <Link to="/membership">Learn About Membership</Link>
              </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-8 text-center">
            Workshop FAQs
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border-b border-stone-200 pb-6">
                <h3 className="text-lg font-semibold text-stone-900 mb-2">{faq.question}</h3>
                <p className="text-stone-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-cafa-red text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-serif mb-4">
            Elevate Your Art with Intensive Training
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join San Antonio&apos;s premier art workshop program and learn from the best.
          </p>
          <Button asChild size="lg" className="bg-white text-cafa-red hover:bg-stone-100">
            <Link to="/workshops">Browse All Workshops</Link>
          </Button>
        </div>
      </section>
    </main>
  );
};

export default ArtWorkshopsSanAntonio;
